import React from 'react';

import { EditOutlined, DeleteOutlined, LinkOutlined } from '@ant-design/icons';
import Can from '@shared/permissions/Can';
import ListPageHeader from '@shared/ListPageHeader';
import { Card, Descriptions, Space, Button, Empty, Table, Popconfirm, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { formatDateTime } from '../../../utils/dateTime';

interface ProductRef {
  id: string;
  name: string;
  sku?: string;
}

interface MerchantRef {
  id?: string;
  name?: string;
}

interface FlexibleProduct {
  id: string;
  name?: string;
  sku?: string;
  mechanism?: string;
  merchant?: MerchantRef | null;
  products?: ProductRef[];
  created_at?: string | null;
  updated_at?: string | null;
}

interface FlexibleProductService {
  delete: (id: string) => Promise<{ success?: boolean; message?: string } | unknown>;
  [key: string]: any;
}

interface FlexibleProductShowViewProps {
  flexibleProduct: FlexibleProduct | null | undefined;
  service: FlexibleProductService;
  role?: 'admin' | 'operator';
}

const FlexibleProductShowView: React.FC<FlexibleProductShowViewProps> = ({
  flexibleProduct,
  service,
  role: _role = 'admin',
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleEdit = () => {
    navigate('edit');
  };

  const handleDelete = async () => {
    if (!flexibleProduct) return;
    try {
      await service.delete(flexibleProduct.id);
      message.success(t('common.success.deleted'));
      const listPath = _role === 'admin' ? '/admin/flexible_products' : '/operator/flexible_products';
      navigate(listPath, { replace: true });
    } catch (error: any) {
      message.error(error?.message || t('common.error.delete_failed'));
    }
  };

  const getMechanismLabel = (mechanism?: string) => {
    if (!mechanism) return '';
    const mechanismMap: Record<string, string> = {
      'equally': t(`shared.flexible_products.mechanism.equally`),
      'most_stock': t(`shared.flexible_products.mechanism.most_stock`),
      'least_stock': t(`shared.flexible_products.mechanism.least_stock`),
    };
    return mechanismMap[mechanism] || mechanism;
  };

  if (!flexibleProduct) {
    return (
      <div>
        <Empty description={t('common.error.no_data')} />
      </div>
    );
  }

  const headerBasePath = _role === 'operator' ? '/operator' : '/admin';

  const productColumns = [
    {
      title: t(`shared.flexible_products.form.products`),
      key: 'product',
      render: (_: unknown, product: ProductRef) => (
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div>
              <Link to={`${headerBasePath}/products/${product.id}`} style={{ wordBreak: 'break-word' }}>
                {product.name}
              </Link>
            </div>
            <div style={{ fontSize: 12, color: '#999' }}>{product.sku}</div>
          </div>
          <a
            href={`${headerBasePath}/products/${product.id}`}
            target="_blank"
            rel="noreferrer"
            style={{ flexShrink: 0 }}
          >
            <LinkOutlined style={{ fontSize: 12, color: '#999' }} />
          </a>
        </div>
      ),
    },
  ];

  const headerActions = (
    <Space>
      <Can requiredPermission="products:write">
        <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
          {t('common.button.edit')}
        </Button>
      </Can>
      <Can requiredPermission="products:write">
        <Popconfirm
          title={t('common.confirm.delete_title')}
          description={t('common.confirm.delete_content', { name: flexibleProduct.name })}
          onConfirm={handleDelete}
          okText={t('common.button.confirm')}
          cancelText={t('common.button.cancel')}
          okButtonProps={{ danger: true }}
        >
          <Button danger icon={<DeleteOutlined />}>
            {t('common.button.delete')}
          </Button>
        </Popconfirm>
      </Can>
    </Space>
  );

  return (
    <div>
      <ListPageHeader
        basePath={headerBasePath}
        items={['products', 'products.flexible', { label: flexibleProduct.name ?? '' }]}
        actions={headerActions}
      />
      <Descriptions column={1} bordered style={{ marginBottom: 16 }}>
        {flexibleProduct.merchant && (
          <Descriptions.Item label={t(`shared.merchant`)}>
            {flexibleProduct.merchant.name || '-'}
          </Descriptions.Item>
        )}
        <Descriptions.Item label={t(`shared.flexible_products.form.name`)}>
          {flexibleProduct.name || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t(`shared.flexible_products.form.sku`)}>
          {flexibleProduct.sku || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t(`shared.flexible_products.form.mechanism`)}>
          {getMechanismLabel(flexibleProduct.mechanism)}
        </Descriptions.Item>
        <Descriptions.Item label={t(`common.created_at`)}>
          {flexibleProduct.created_at ? formatDateTime(flexibleProduct.created_at) : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t(`common.updated_at`)}>
          {flexibleProduct.updated_at ? formatDateTime(flexibleProduct.updated_at) : '-'}
        </Descriptions.Item>
      </Descriptions>
      <Card title={t(`shared.flexible_products.detail.products`)}>
        {flexibleProduct.products && flexibleProduct.products.length > 0 ? (
          <Table
            columns={productColumns}
            dataSource={flexibleProduct.products}
            rowKey="id"
            pagination={false}
            showHeader={false}
          />
        ) : (
          <Empty description={t(`shared.flexible_products.detail.no_products`)} />
        )}
      </Card>
    </div>
  );
};

export default FlexibleProductShowView;
